<script setup>
// SiteUp (M9): cartão de álbum do WhatsApp.
//
// O anúncio chega como texto; as mídias vêm nas mensagens seguintes.
// MessageList agrupa e esconde as mídias individuais. Aqui só desenhamos
// a grade e abrimos a GalleryView já existente (setas + "2 / 3").
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { albumCounts } from 'dashboard/helper/albumGrouping';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { contentAttributes, content, albumAttachments } = useMessageContext();
const { t } = useI18n();

const showGallery = ref(false);
const activeAttachment = ref(null);

const counts = computed(() =>
  albumCounts({
    content: content.value,
    contentAttributes: contentAttributes.value,
  })
);

const items = computed(() => albumAttachments?.value || []);

const snakeItems = computed(() => useSnakeCase(items.value));

const countLine = computed(() => {
  const parts = [];
  if (counts.value.imageCount > 0) {
    parts.push(t('CONVERSATION.ALBUM.IMAGES', counts.value.imageCount));
  }
  if (counts.value.videoCount > 0) {
    parts.push(t('CONVERSATION.ALBUM.VIDEOS', counts.value.videoCount));
  }
  if (!parts.length && counts.value.totalCount > 0) {
    parts.push(t('CONVERSATION.ALBUM.FILES', counts.value.totalCount));
  }
  return parts.join(' · ');
});

const openGallery = item => {
  activeAttachment.value = useSnakeCase(item);
  showGallery.value = true;
};

const previewSrc = item =>
  item.thumbUrl || item.dataUrl || item.thumb_url || item.data_url;

const isVideo = item => {
  const type = item.fileType || item.file_type;
  return type === 'video' || type === 'ig_reel';
};
</script>

<template>
  <BaseBubble class="p-3" data-bubble-name="album">
    <div class="flex flex-col gap-2 min-w-[15rem]">
      <div class="flex items-center gap-1.5 opacity-80">
        <span class="flex size-4 shrink-0 items-center justify-center">
          <Icon icon="i-ph-images" class="size-4" />
        </span>
        <span class="text-xs font-medium uppercase tracking-wide">
          {{ t('CONVERSATION.ALBUM.TITLE') }}
        </span>
      </div>

      <p v-if="countLine" class="text-sm font-medium tabular-nums">
        {{ countLine }}
      </p>
      <p v-else-if="!items.length" class="text-sm opacity-70">
        {{ t('CONVERSATION.ALBUM.RECEIVING') }}
      </p>

      <ul v-if="items.length" class="grid grid-cols-2 gap-1 m-0 p-0 list-none">
        <li v-for="item in items" :key="item.id || previewSrc(item)">
          <button
            type="button"
            class="relative block w-full overflow-hidden rounded-lg aspect-square min-h-11"
            :aria-label="t('CONVERSATION.ALBUM.OPEN')"
            @click="openGallery(item)"
          >
            <img
              :src="previewSrc(item)"
              alt=""
              class="size-full object-cover skip-context-menu"
            />
            <span
              v-if="isVideo(item)"
              class="absolute inset-0 flex items-center justify-center bg-n-slate-12/30"
              aria-hidden="true"
            >
              <Icon icon="i-ph-play-fill" class="size-6" />
            </span>
          </button>
        </li>
      </ul>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && activeAttachment"
    v-model:show="showGallery"
    :attachment="activeAttachment"
    :all-attachments="snakeItems"
    @close="showGallery = false"
  />
</template>
